import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Card, Divider, IconButton, Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import clsx from 'clsx';
import { Command } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { IcClose } from '@/assets/images';
import { useUserStore } from '@/store';

import { UPDATE_UNIT_SHIFT_STATUS_COMMAND_ID } from './commands/UpdateUnitShiftStatusCommand';
import { CommandForm } from './components/CommandForm';
import { CommandSelector } from './components/CommandSelector';
import { SystemResponseList } from './components/SystemResponseList';
import {
  createGoToCommand,
  createUpdateUnitShiftStatusCommand,
  updateUnitStatusCommand,
} from './data';
import { RootModal } from './style';
import { CommandDefinition, CommandHistoryEntry } from './types';

// Constants for command history
const HISTORY_STORAGE_KEY = 'cli-widget-command-history';
const MAX_HISTORY_SIZE = 10;

// Constants for storing widget position
const POSITION_STORAGE_KEY = 'cli-widget-position';
const DEFAULT_POSITION = { x: 0, y: 0 };

// Helper functions for managing persistent command history
const loadCommandHistory = (): CommandHistoryEntry[] => {
  try {
    const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Convert timestamp strings back to Date objects
      return parsed.map((entry: any) => ({
        ...entry,
        timestamp: new Date(entry.timestamp),
      }));
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('🚀 ~ error:', error);
  }
  return [];
};

const saveCommandHistory = (history: CommandHistoryEntry[]): void => {
  try {
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
  } catch (error) {
    // Silently fail
  }
};

// Helper functions for managing widget position
const loadWidgetPosition = (): { x: number; y: number } => {
  try {
    const stored = localStorage.getItem(POSITION_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Error loading widget position:', error);
  }

  return DEFAULT_POSITION;
};

const saveWidgetPosition = (position: { x: number; y: number }): void => {
  try {
    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
  } catch (error) {
    // Silently fail
  }
};

const addToHistory = (
  currentHistory: CommandHistoryEntry[],
  newEntry: CommandHistoryEntry,
): CommandHistoryEntry[] => {
  // Add new entry and keep only the last MAX_HISTORY_SIZE entries
  const updatedHistory = [...currentHistory, newEntry].slice(-MAX_HISTORY_SIZE);
  saveCommandHistory(updatedHistory);
  return updatedHistory;
};

// Utility function to clear persistent history (for development/debugging)
// const clearCommandHistory = (): void => {
//   localStorage.removeItem(HISTORY_STORAGE_KEY);
// };

/**
 * CLIWidget - Command Line Interface Widget
 *
 * Features:
 * - Ctrl/Cmd + / : Toggle CLI widget open/close
 * - Ctrl/Cmd + ↑ : Navigate backwards through command history (older commands)
 * - Ctrl/Cmd + ↓ : Navigate forwards through command history (newer commands)
 * - Delete : Clear all fields and reset to command selector
 *
 * History management:
 * - Stores up to 10 successful commands persistently across sessions
 * - History is maintained in localStorage and survives widget close/reopen
 * - Only successful command executions are added to history
 * - Navigate through previously used unit and status combinations
 * - Efficiently repeat common operations
 */

interface SystemResponse {
  id: string;
  type: 'success' | 'error';
  message: string;
  date: Date;
  data?: any;
}

export default function CLIWidget() {
  const [open, setOpen] = useState(false);
  const { account } = useUserStore();
  const navigate = useNavigate();
  const handleClose = () => setOpen(false);
  const [systemResponses, setSystemResponses] = useState<SystemResponse[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [position, setPosition] = useState(() => loadWidgetPosition());
  const [isDragging, setIsDragging] = useState(false);

  const [selectedCommandId, setSelectedCommandId] = useState<string | null>(
    null,
  );
  const [commandsHistory, setCommandsHistory] =
    useState<CommandHistoryEntry[]>(loadCommandHistory);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);
  const [currentValues, setCurrentValues] = useState<Record<string, any>>({});
  const [isExecuting, setIsExecuting] = useState(false);
  const [commands, setCommands] = useState<CommandDefinition[]>([]);
  const [areInputFieldsEmpty, setAreInputFieldsEmpty] = useState<boolean>(true);
  const [isCommandSelectorEmpty, setIsCommandSelectorEmpty] =
    useState<boolean>(true);

  // Find the selected command from our commands array
  const selectedCommand = selectedCommandId
    ? commands.find((cmd) => cmd.id === selectedCommandId)
    : null;

  // Update areInputFieldsEmpty when selectedCommand changes
  useEffect(() => {
    // If no command is selected, consider fields empty
    if (!selectedCommand) {
      setAreInputFieldsEmpty(true);
    }
  }, [selectedCommand]);

  // Initialize commands
  useEffect(() => {
    const availableCommands: CommandDefinition[] = [];

    // Add GoTo command (always available)
    availableCommands.push(createGoToCommand(navigate));

    // Add Update Unit Shift Status command (always available)
    availableCommands.push(createUpdateUnitShiftStatusCommand(account));

    // Add unit status command if enabled
    if (account?.settings.features?.isEnabledCustomUnitStatus) {
      availableCommands.push(updateUnitStatusCommand);
    }

    setCommands(availableCommands);
  }, [account, navigate]);

  useEffect(() => {
    // This is to ensure that the system responses are cleared when the modal is closed
    if (!open) {
      setSystemResponses([]);
      // Reset command selector state when closing
      setIsCommandSelectorEmpty(true);
    } else {
      // Focus the input when the modal opens
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100); // Small timeout to ensure the modal is fully rendered
    }
  }, [open]);

  // Add a direct event listener for the key combination
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setSelectedCommandId(null);
        setCurrentValues({});
        setHistoryIndex(-1);
        setIsCommandSelectorEmpty(true);
        setOpen((prev) => !prev);
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key === 'ArrowUp' &&
        areInputFieldsEmpty &&
        isCommandSelectorEmpty
      ) {
        e.preventDefault();
        navigateHistory('up');
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key === 'ArrowDown' &&
        areInputFieldsEmpty &&
        isCommandSelectorEmpty
      ) {
        e.preventDefault();
        navigateHistory('down');
      }
      if (e.key === 'Delete' && open) {
        e.preventDefault();
        // Clear all fields and reset state
        setSelectedCommandId(null);
        setCurrentValues({});
        setHistoryIndex(-1);
        setAreInputFieldsEmpty(true); // Ensure we know fields are empty after reset
        setIsCommandSelectorEmpty(true); // Reset command selector state
        // Focus back to command selector
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    commandsHistory,
    historyIndex,
    areInputFieldsEmpty,
    isCommandSelectorEmpty,
  ]);

  const navigateHistory = (direction: 'up' | 'down') => {
    if (commandsHistory.length === 0) return;

    let newIndex = historyIndex;

    if (direction === 'up') {
      // Navigate backwards in history (older commands)
      if (historyIndex === -1) {
        newIndex = commandsHistory.length - 1; // Start from the most recent
      } else {
        newIndex = Math.max(0, historyIndex - 1); // Go to older commands
      }
    } else {
      // Navigate forwards in history (newer commands)
      if (historyIndex === -1) {
        return; // Already at current state, can't go further
      } else if (historyIndex === commandsHistory.length - 1) {
        newIndex = -1; // Go back to current/empty state
      } else {
        newIndex = historyIndex + 1; // Go to newer commands
      }
    }

    setHistoryIndex(newIndex);

    if (newIndex === -1) {
      // Clear selection and values
      setSelectedCommandId(null);
      setCurrentValues({});
    } else {
      // Load command and values from history
      const historyEntry = commandsHistory[newIndex];
      setSelectedCommandId(historyEntry.commandId);
      setCurrentValues(historyEntry.values);
    }
  };

  const handleSelectCommand = (commandId: string) => {
    setSelectedCommandId(commandId);
    setHistoryIndex(-1); // Reset history navigation when manually selecting
    setCurrentValues({}); // Clear any values from history
    // Fields are initially empty when selecting a command
    setAreInputFieldsEmpty(true);
    setIsCommandSelectorEmpty(true); // Reset command selector state after selection
  };

  const handleFocusCommandSelector = () => {
    // Focus the command selector input
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleExecuteCommand = async (values: Record<string, any>) => {
    if (!selectedCommand) return;

    setIsExecuting(true);

    try {
      const response = await selectedCommand.execute(values);

      setSystemResponses((prev) => [
        {
          id: uuidv4(),
          type: response.success ? 'success' : 'error',
          message: response.message,
          date: new Date(),
          data: response.data,
        },
        ...prev,
      ]);

      // Only add to history if command was successful
      if (response.success) {
        const historyEntry: CommandHistoryEntry = {
          commandId: selectedCommandId || '',
          values: { ...values },
          timestamp: new Date(),
        };
        setCommandsHistory((prev) => addToHistory(prev, historyEntry));
      }
    } catch (error) {
      setSystemResponses((prev) => [
        {
          id: uuidv4(),
          type: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'An unknown error occurred',
          date: new Date(),
        },
        ...prev,
      ]);
    } finally {
      setIsExecuting(false);
      setSelectedCommandId(null); // Reset the selected command after execution
      setCurrentValues({}); // Clear current values
      setHistoryIndex(-1); // Reset history index
      setAreInputFieldsEmpty(true); // Ensure fields are marked as empty
      setIsCommandSelectorEmpty(true); // Reset command selector state
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  // Handle drag operations
  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    // Only handle drag if clicking on the header but not on the close button
    if ((event.target as HTMLElement).closest('.btn-close')) {
      return;
    }

    setIsDragging(true);

    const startX = event.clientX;
    const startY = event.clientY;
    const startLeft = position.x;
    const startTop = position.y;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      const newPosition = {
        x: startLeft + deltaX,
        y: startTop + deltaY,
      };

      setPosition(newPosition);

      // Apply the position directly to the modal content
      const modalContent = document.querySelector(
        '.modal-content',
      ) as HTMLElement;
      if (modalContent) {
        modalContent.style.transform = `translate(calc(-50% + ${newPosition.x}px), calc(-50% + ${newPosition.y}px))`;
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);

      // Save the position to localStorage
      saveWidgetPosition(position);

      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Apply saved position when the component mounts
  useEffect(() => {
    if (open) {
      const modalContent = document.querySelector(
        '.modal-content',
      ) as HTMLElement;
      if (modalContent && position.x !== 0 && position.y !== 0) {
        modalContent.style.transform = `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`;
      }
    }
  }, [open, position]);

  return (
    <RootModal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      position={position}
      wide={selectedCommand?.id === UPDATE_UNIT_SHIFT_STATUS_COMMAND_ID}
    >
      <Card className={clsx('modal-content')}>
        <Typography
          fontSize={16}
          fontWeight={500}
          className={clsx('header', { dragging: isDragging })}
          onMouseDown={handleMouseDown}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Command /> Command Line
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            className="btn-close"
            tabIndex={-1}
          >
            <IcClose />
          </IconButton>
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={2}>
          {selectedCommand ? (
            <>
              <Box display="flex" width="100%" alignItems="stretch">
                <Box flexGrow={1} flexBasis={0} minWidth={0}>
                  <CommandSelector
                    commands={commands}
                    selectedCommandId={selectedCommandId}
                    onSelectCommand={handleSelectCommand}
                    inputRef={inputRef}
                    noRightBorder={true} // Remove right border for better alignment
                    onInputEmpty={setIsCommandSelectorEmpty}
                  />
                </Box>
                <CommandForm
                  command={selectedCommand}
                  onExecute={handleExecuteCommand}
                  isLoading={isExecuting}
                  useHorizontalLayout={true}
                  initialValues={currentValues}
                  onFocusCommandSelector={handleFocusCommandSelector}
                  onAreInputFieldsEmpty={setAreInputFieldsEmpty}
                />
              </Box>
            </>
          ) : (
            <CommandSelector
              commands={commands}
              selectedCommandId={selectedCommandId}
              onSelectCommand={handleSelectCommand}
              inputRef={inputRef}
              onInputEmpty={setIsCommandSelectorEmpty}
            />
          )}
          <SystemResponseList responses={systemResponses} />
        </Stack>
      </Card>
    </RootModal>
  );
}
